import { useEffect } from "react"

import { useNavigate } from "react-router-dom"

import type { TranslatorToolControl } from "../translator-tool-utils"
import type { useAIExplorerStore } from "@/stores/ai-explorer-store"

import { useRoutes } from "@/contexts/use-routes"

import { useAITranslatorSettingsLocal } from "./use-ai-translator-settings-local"
import { useTranslateSettingsPanel } from "./use-translate-settings-panel"
import { buildTranslatorRequest, selectedTranslationLanguages } from "../translator-tool-utils"

export function useTranslatorTool(
  store: ReturnType<typeof useAIExplorerStore>,
  available: boolean
): TranslatorToolControl | undefined {
  const { settings, updateSettings } = useAITranslatorSettingsLocal()
  const navigate = useNavigate()
  const { routes } = useRoutes()
  const historyPath = routes.find((route) => route.component === "translate")?.path ?? "/translate"
  const { toggleSettingsPanel } = useTranslateSettingsPanel(true)
  const request = store((state) => state.translationRequest)
  const enabled = request !== null && available
  const selectedLanguages = selectedTranslationLanguages(settings)

  useEffect(() => {
    if (request && !available) store.getState().setTranslationRequest(null)
  }, [available, request, store])

  useEffect(() => {
    if (enabled && selectedTranslationLanguages(settings).length > 0) {
      const next = buildTranslatorRequest(settings)
      // A debounced DB echo of the same preferences is not a new selection.
      if (JSON.stringify(next) !== JSON.stringify(store.getState().translationRequest)) {
        store.getState().setTranslationRequest(next)
      }
    }
  }, [enabled, settings, store])

  if (!available) return undefined
  return {
    enabled,
    availableLanguages: settings.availableLanguages,
    selectedLanguages,
    glossaryLanguage: settings.selectedLanguage,
    onEnabledChange(next) {
      store.getState().setTranslationRequest(next ? buildTranslatorRequest(settings) : null)
    },
    onLanguageChange(language, checked) {
      const next = checked
        ? [...selectedLanguages, language]
        : selectedLanguages.filter((item) => item !== language)
      if (next.length === 0 || next.length > 10) return
      updateSettings({ selectedLanguages: next })
      if (enabled)
        store
          .getState()
          .setTranslationRequest(buildTranslatorRequest({ ...settings, selectedLanguages: next }))
    },
    onAddLanguage(value) {
      const language = value.trim()
      if (!language || language.length > 80) return "invalid"
      if (
        settings.availableLanguages.some(
          (item) => item.toLocaleLowerCase() === language.toLocaleLowerCase()
        )
      )
        return "duplicate"
      const next = {
        ...settings,
        availableLanguages: [...settings.availableLanguages, language],
        selectedLanguages:
          selectedLanguages.length < 10 ? [...selectedLanguages, language] : selectedLanguages,
      }
      updateSettings(next)
      if (enabled) store.getState().setTranslationRequest(buildTranslatorRequest(next))
      return undefined
    },
    onAdvanced: toggleSettingsPanel,
    onHistory: () => {
      void navigate(historyPath)
    },
  }
}
